var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import FormField from "@widget/forms/inputs/FormField";
import Label from "@widget/forms/inputs/Label";
import Input from "@widget/forms/inputs/Input";
import TextArea from "@widget/forms/inputs/TextArea";
import SearchBox from "@widget/forms/inputs/SearchBox";
import TagInput from "@widget/forms/inputs/TagInput";
import Select from "@widget/forms/inputs/Select";
import MultiSelect from "@widget/forms/inputs/MultiSelect";
import AutoCompleteInput from "@widget/forms/inputs/AutoComplete";
import Checkbox from "@widget/forms/inputs/Checkbox";
import ToggleFilter from "@widget/forms/inputs/ToggleFilter";
import DatePicker from "@widget/forms/inputs/DateTimePicker/DatePicker";
import TimePicker from "@widget/forms/inputs/DateTimePicker/TimePicker";
import DateRangePicker from "@widget/forms/inputs/DateTimePicker/DateRangePicker";
import DateTimePicker from "@widget/forms/inputs/DateTimePicker/DateTimePicker";
import TimeRangePicker from "@widget/forms/inputs/DateTimePicker/TimeRangePicker";
import ColorPicker from "@widget/forms/inputs/ColorPicker/ColorPicker";
import IconPicker from "@widget/forms/inputs/IconPicker/IconPicker";
import FileInput from "@widget/forms/inputs/FileInput/FileInput";
import InfoCard from "@widget/list/InfoCard";
import { ObjectSearchComponent } from "@widget/list/Tables/object-search/ObjectSearchComponent";
import { SimpleConfigurationTable } from "@widget/forms/SimpleConfigurationTable/SimpleConfigurationTable";
import { ConfigurationView } from "@widget/forms/ConfigurationView/ConfigurationView";
import { UXPContext } from "@core/context/Context";
import ButtonComponent from "@widget/buttons/ButtonComponent";
import CodeEditor from "@widget/forms/inputs/CodeEditor/CodeEditor";
import MarkdownEditor from "@widget/forms/inputs/MarkdownEditor/MarkdownEditor";
import PillInput from "@widget/forms/inputs/PillInput/PillInput";
import Chip from "@widget/other/Chip";
// ─── shared data ──────────────────────────────────────────────────────────────
const FRUIT_OPTIONS = ['Apple', 'Banana', 'Cherry', 'Date', 'Elderberry', 'Fig', 'Grape', 'Kiwi', 'Lemon', 'Mango'];
const COUNTRY_OPTIONS = [
    { label: 'India', value: 'IN' },
    { label: 'Japan', value: 'JP' },
    { label: 'China', value: 'CN' },
    { label: 'Singapore', value: 'SG' },
    { label: 'Italy', value: 'IT' },
    { label: 'France', value: 'FR' },
    { label: 'Germany', value: 'DE' },
    { label: 'Brazil', value: 'BR' },
    { label: 'Australia', value: 'AU' },
    { label: 'Canada', value: 'CA' },
];
const STATUS_OPTIONS = [
    { label: 'Active', value: 'active' },
    { label: 'Inactive', value: 'inactive' },
    { label: 'Pending', value: 'pending' },
];
// ─── Section wrapper ──────────────────────────────────────────────────────────
const Section = ({ title, children }) => (_jsxs("div", { style: {
        padding: '1.5rem',
        minWidth: 320,
        maxWidth: 480,
        flex: '1 1 320px',
        borderRadius: '0.5rem',
        background: 'var(--secondaryBGColor)',
    }, children: [_jsx("h4", { style: { margin: '0 0 1.25rem 0', color: 'var(--primaryTextColor)', borderBottom: '1px solid var(--borderColor)', paddingBottom: '0.5rem' }, children: title }), _jsx("div", { style: { display: 'flex', flexDirection: 'column', gap: '1rem' }, children: children })] }));
const Value = ({ label, children }) => (_jsxs("div", { style: { fontSize: '0.75rem', color: 'var(--secondaryTextColor)', marginTop: '0.25rem' }, children: [label ? `${label}: ` : '', children] }));
// ─── Featured: Input Addon Demo ───────────────────────────────────────────────
const InputAddonDemo = () => {
    const [price, setPrice] = useState('');
    const [currency, setCurrency] = useState('USD');
    const [weight, setWeight] = useState('');
    const [unit, setUnit] = useState('kg');
    const [domain, setDomain] = useState('');
    const [phone, setPhone] = useState('');
    const [dialCode, setDialCode] = useState('+1');
    const [phoneType, setPhoneType] = useState('mobile');
    return (_jsxs("div", { style: {
            width: '100%',
            padding: '1.5rem',
            borderRadius: '0.5rem',
            background: 'var(--secondaryBGColor)',
            display: 'flex',
            flexDirection: 'column',
            gap: '1.25rem',
        }, children: [_jsx("h3", { style: { margin: 0, color: 'var(--primaryTextColor)', borderBottom: '1px solid var(--borderColor)', paddingBottom: '0.5rem' }, children: "Input \u2014 Addon Examples" }), _jsxs("div", { style: { display: 'flex', flexWrap: 'wrap', gap: '1.5rem', alignItems: 'flex-start' }, children: [_jsxs("div", { style: { flex: '1 1 220px', display: 'flex', flexDirection: 'column', gap: '0.5rem' }, children: [_jsx(Label, { children: "Prefix dropdown (currency selector)" }), _jsx(Input, { value: price, prefix: {
                                    options: [
                                        { label: 'USD', value: 'USD', icon: 'fas dollar-sign' },
                                        { label: 'EUR', value: 'EUR', icon: 'fas euro-sign' },
                                        { label: 'GBP', value: 'GBP', icon: 'fas pound-sign' },
                                        { label: 'JPY', value: 'JPY', icon: 'fas yen-sign' },
                                    ],
                                    selected: currency,
                                    placeholder: 'Currency',
                                }, onChange: (val, prefix) => {
                                    setPrice(val);
                                    if (prefix !== undefined)
                                        setCurrency(prefix);
                                }, placeholder: "0.00", fullWidth: true }), _jsx(Value, { children: `currency: ${currency} | value: "${price}"` })] }), _jsxs("div", { style: { flex: '1 1 220px', display: 'flex', flexDirection: 'column', gap: '0.5rem' }, children: [_jsx(Label, { children: "Suffix dropdown (unit selector)" }), _jsx(Input, { value: weight, suffix: {
                                    options: [
                                        { label: 'kg', value: 'kg' },
                                        { label: 'lbs', value: 'lbs' },
                                        { label: 'g', value: 'g' },
                                        { label: 'oz', value: 'oz' },
                                    ],
                                    selected: unit,
                                    placeholder: 'Unit',
                                }, onChange: (val, _prefix, suffix) => {
                                    setWeight(val);
                                    if (suffix !== undefined)
                                        setUnit(suffix);
                                }, placeholder: "0", fullWidth: true }), _jsx(Value, { children: `unit: ${unit} | value: "${weight}"` })] }), _jsxs("div", { style: { flex: '1 1 220px', display: 'flex', flexDirection: 'column', gap: '0.5rem' }, children: [_jsx(Label, { children: "String prefix + suffix with separator" }), _jsx(Input, { value: domain, onChange: (val) => setDomain(val), prefix: { value: 'https://', showSeparator: true }, suffix: { value: '.com', showSeparator: true }, placeholder: "yourdomain", fullWidth: true }), _jsx(Value, { children: `value: "${domain}" → https://${domain}.com` })] }), _jsxs("div", { style: { flex: '1 1 220px', display: 'flex', flexDirection: 'column', gap: '0.5rem' }, children: [_jsx(Label, { children: "Both prefix + suffix dropdowns" }), _jsx(Input, { value: phone, prefix: {
                                    options: [
                                        { label: '+1 (US/CA)', value: '+1' },
                                        { label: '+44 (UK)', value: '+44' },
                                        { label: '+61 (AU)', value: '+61' },
                                        { label: '+91 (IN)', value: '+91' },
                                        { label: '+49 (DE)', value: '+49' },
                                        { label: '+33 (FR)', value: '+33' },
                                        { label: '+81 (JP)', value: '+81' },
                                        { label: '+86 (CN)', value: '+86' },
                                        { label: '+55 (BR)', value: '+55' },
                                        { label: '+7 (RU)', value: '+7' },
                                        { label: '+971 (AE)', value: '+971' },
                                        { label: '+65 (SG)', value: '+65' },
                                    ],
                                    selected: dialCode,
                                    placeholder: 'Code',
                                }, suffix: {
                                    options: [
                                        { label: 'Mobile', value: 'mobile' },
                                        { label: 'Work', value: 'work' },
                                        { label: 'Home', value: 'home' },
                                    ],
                                    selected: phoneType,
                                    placeholder: 'Type',
                                }, onChange: (val, prefix, suffix) => {
                                    setPhone(val);
                                    if (prefix !== undefined)
                                        setDialCode(prefix);
                                    if (suffix !== undefined)
                                        setPhoneType(suffix);
                                }, placeholder: "Phone number", fullWidth: true }), _jsx(Value, { children: `dialCode: ${dialCode} | type: ${phoneType} | value: "${phone}"` })] })] })] }));
};
// ─── Text Inputs ──────────────────────────────────────────────────────────────
const TextInputsExample = () => {
    const [basic, setBasic] = useState('');
    const [prefixed, setPrefixed] = useState('example.com');
    const [password, setPassword] = useState('secret123');
    const [clearable, setClearable] = useState('Clear me!');
    const [textarea, setTextarea] = useState('Multi-line text here.\nLine two.');
    const [strPrefix, setStrPrefix] = useState('42');
    const [strSuffix, setStrSuffix] = useState('100');
    const [currency, setCurrency] = useState('250');
    const [currencyUnit, setCurrencyUnit] = useState('USD');
    const [weight, setWeight] = useState('75');
    const [weightUnit, setWeightUnit] = useState('kg');
    return (_jsxs(Section, { title: "Text Inputs", children: [_jsxs(FormField, { children: [_jsx(Label, { children: "Basic" }), _jsx(Input, { value: basic, onChange: setBasic, placeholder: "Type something\u2026", fullWidth: true })] }), _jsxs(FormField, { children: [_jsx(Label, { children: "With prefix (ReactNode)" }), _jsx(Input, { value: prefixed, onChange: setPrefixed, prefix: _jsx("span", { style: { fontSize: '0.85rem' }, children: "https://" }), fullWidth: true })] }), _jsxs(FormField, { children: [_jsx(Label, { children: "String prefix with separator" }), _jsx(Input, { value: strPrefix, onChange: setStrPrefix, prefix: { value: '$ ', showSeparator: true }, placeholder: "0", fullWidth: true }), _jsx(Value, { children: `value: "${strPrefix}"` })] }), _jsxs(FormField, { children: [_jsx(Label, { children: "String suffix with separator" }), _jsx(Input, { value: strSuffix, onChange: setStrSuffix, suffix: { value: ' kg', showSeparator: true }, placeholder: "0", fullWidth: true }), _jsx(Value, { children: `value: "${strSuffix}"` })] }), _jsxs(FormField, { children: [_jsx(Label, { children: "Prefix dropdown (currency)" }), _jsx(Input, { value: currency, prefix: {
                            options: [
                                { label: 'USD', value: 'USD' },
                                { label: 'EUR', value: 'EUR' },
                                { label: 'GBP', value: 'GBP' },
                            ],
                            selected: currencyUnit,
                            placeholder: 'Currency'
                        }, onChange: (val, prefix) => {
                            setCurrency(val);
                            if (prefix !== undefined)
                                setCurrencyUnit(prefix);
                        }, placeholder: "Amount", fullWidth: true }), _jsx(Value, { children: `value: "${currency}" | unit: "${currencyUnit}"` })] }), _jsxs(FormField, { children: [_jsx(Label, { children: "Suffix dropdown (units)" }), _jsx(Input, { value: weight, suffix: {
                            options: [
                                { label: 'kg', value: 'kg' },
                                { label: 'lbs', value: 'lbs' },
                                { label: 'g', value: 'g' },
                            ],
                            selected: weightUnit,
                            placeholder: 'Unit'
                        }, onChange: (val, _prefix, suffix) => {
                            setWeight(val);
                            if (suffix !== undefined)
                                setWeightUnit(suffix);
                        }, placeholder: "Weight", fullWidth: true }), _jsx(Value, { children: `value: "${weight}" | unit: "${weightUnit}"` })] }), _jsxs(FormField, { children: [_jsx(Label, { children: "Password" }), _jsx(Input, { type: "password", value: password, onChange: setPassword, fullWidth: true })] }), _jsxs(FormField, { children: [_jsx(Label, { children: "With clear button (auto)" }), _jsx(Input, { value: clearable, onChange: setClearable, placeholder: "Start typing\u2026", fullWidth: true })] }), _jsxs(FormField, { children: [_jsx(Label, { children: "Disabled" }), _jsx(Input, { value: "Cannot edit this", onChange: () => { }, disabled: true, fullWidth: true })] }), _jsxs(FormField, { children: [_jsx(Label, { children: "TextArea (clear auto)" }), _jsx(TextArea, { value: textarea, onChange: setTextarea, rows: 3, fullWidth: true })] }), _jsx(Value, { children: `basic: "${basic}"` })] }));
};
// ─── Search ───────────────────────────────────────────────────────────────────
const SearchExample = () => {
    const [query, setQuery] = useState('');
    const [collapsed, setCollapsed] = useState('');
    return (_jsxs(Section, { title: "Search Box", children: [_jsxs(FormField, { children: [_jsx(Label, { children: "Normal" }), _jsx(SearchBox, { value: query, onChange: setQuery, placeholder: "Search\u2026", fullWidth: true })] }), _jsxs(FormField, { children: [_jsx(Label, { children: "Collapsed" }), _jsx(SearchBox, { value: collapsed, onChange: setCollapsed, collapsed: true })] }), _jsx(Value, { children: `query: "${query}"` })] }));
};
// ─── Tag Input ────────────────────────────────────────────────────────────────
const TagInputExample = () => {
    const [tags, setTags] = useState(['React', 'TypeScript']);
    const [fruitTags, setFruitTags] = useState([]);
    return (_jsxs(Section, { title: "Tag Input", children: [_jsxs(FormField, { children: [_jsx(Label, { children: "Free-text tags (Enter or comma to add)" }), _jsx(TagInput, { value: tags, onChange: setTags, placeholder: "Type and press Enter\u2026", fullWidth: true })] }), _jsxs(FormField, { children: [_jsx(Label, { children: "With suggestions (static list)" }), _jsx(TagInput, { value: fruitTags, onChange: setFruitTags, options: FRUIT_OPTIONS, placeholder: "Type to search fruits\u2026", fullWidth: true })] }), _jsx(Value, { children: `[${tags.map(t => `"${t}"`).join(', ')}]` })] }));
};
// ─── Select ───────────────────────────────────────────────────────────────────
const SelectExample = () => {
    const [selected, setSelected] = useState('');
    const [selectedClear, setSelectedClear] = useState('SG');
    const [selectedCustom, setSelectedCustom] = useState('');
    const [selectedValid, setSelectedValid] = useState('');
    return (_jsxs(Section, { title: "Select", children: [_jsxs(FormField, { children: [_jsx(Label, { children: "Basic" }), _jsx(Select, { options: COUNTRY_OPTIONS, selected: selected, onChange: setSelected, placeholder: "Choose a country\u2026", fullWidth: true })] }), _jsxs(FormField, { children: [_jsx(Label, { children: "With clear button (auto)" }), _jsx(Select, { options: COUNTRY_OPTIONS, selected: selectedClear, onChange: setSelectedClear, placeholder: "Choose a country\u2026", fullWidth: true })] }), _jsxs(FormField, { children: [_jsx(Label, { children: "Validation state" }), _jsx(Select, { options: COUNTRY_OPTIONS, selected: selectedValid, onChange: setSelectedValid, isValid: selectedValid ? true : false, placeholder: "Pick one to see valid state\u2026", fullWidth: true })] }), _jsxs(FormField, { children: [_jsx(Label, { children: "Custom renderOption (arrow keys + highlight)" }), _jsx(Select, { options: COUNTRY_OPTIONS, selected: selectedCustom, onChange: setSelectedCustom, placeholder: "Navigate with arrow keys\u2026", fullWidth: true, renderOption: (opt, _key, isHighlighted, isSelected) => (_jsxs("div", { style: {
                                padding: '0.5rem 0.75rem',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem',
                                color: isHighlighted ? 'var(--highlightTextColor)' : 'var(--primaryTextColor)',
                            }, children: [_jsx("span", { style: { width: '1rem', textAlign: 'center' }, children: isSelected ? '✓' : isHighlighted ? '›' : '' }), opt.label] })) })] }), _jsxs(FormField, { children: [_jsx(Label, { children: "Disabled" }), _jsx(Select, { options: COUNTRY_OPTIONS, selected: "JP", onChange: () => { }, disabled: true, fullWidth: true })] }), _jsx(Value, { children: `selected: "${selected}" | custom: "${selectedCustom}"` })] }));
};
// ─── MultiSelect ──────────────────────────────────────────────────────────────
const MultiSelectExample = () => {
    const [selected, setSelected] = useState([]);
    const [preSelected, setPreSelected] = useState(['FR', 'DE']);
    return (_jsxs(Section, { title: "MultiSelect", children: [_jsxs(FormField, { children: [_jsx(Label, { children: "Basic" }), _jsx(MultiSelect, { options: COUNTRY_OPTIONS, selected: selected, onChange: setSelected, placeholder: "Choose countries\u2026", fullWidth: true })] }), _jsxs(FormField, { children: [_jsx(Label, { children: "Pre-selected values" }), _jsx(MultiSelect, { options: COUNTRY_OPTIONS, selected: preSelected, onChange: setPreSelected, placeholder: "Choose countries\u2026", fullWidth: true })] }), _jsxs(FormField, { children: [_jsx(Label, { children: "Disabled" }), _jsx(MultiSelect, { options: COUNTRY_OPTIONS, selected: ['IN', 'JP'], onChange: () => { }, disabled: true, fullWidth: true })] }), _jsx(Value, { children: `[${selected.join(', ')}]` })] }));
};
// ─── AutoComplete ─────────────────────────────────────────────────────────────
const AutoCompleteExample = () => {
    const [val1, setVal1] = useState('');
    const [val2, setVal2] = useState('');
    const inputRef = useRef(null);
    const countryNames = COUNTRY_OPTIONS.map(c => c.label);
    function renderCustomAutoFill() {
        const matches = countryNames.filter(c => !val2 || c.toLowerCase().includes(val2.toLowerCase()));
        return (_jsx("div", { children: matches.map((c, i) => (_jsx("div", { className: "uxpcore_autocomplete__suggestion-item", onMouseDown: (e) => e.preventDefault(), onClick: () => { var _a; setVal2(c); (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.close(); }, children: c }, i))) }));
    }
    return (_jsxs(Section, { title: "AutoComplete", children: [_jsxs(FormField, { children: [_jsx(Label, { children: "Static options (built-in filtering)" }), _jsx(AutoCompleteInput, { value: val1, onChange: setVal1, options: countryNames, placeholder: "Type to filter countries\u2026", fullWidth: true })] }), _jsxs(FormField, { children: [_jsx(Label, { children: "Custom autoFill (arrow keys + Enter work)" }), _jsx(AutoCompleteInput, { value: val2, onChange: setVal2, autoFill: renderCustomAutoFill, placeholder: "Type to filter countries\u2026", fullWidth: true, ref: inputRef })] }), _jsx(Value, { children: `static: "${val1}" | custom: "${val2}"` })] }));
};
// ─── Checkbox ─────────────────────────────────────────────────────────────────
const CheckboxExample = () => {
    const [checked, setChecked] = useState(false);
    const [switchLine, setSwitchLine] = useState(true);
    const [switchBox, setSwitchBox] = useState(false);
    const [intermediate, setIntermediate] = useState('intermediate');
    return (_jsxs(Section, { title: "Checkbox", children: [_jsx(FormField, { inline: true, children: _jsx(Checkbox, { checked: checked, onChange: setChecked, label: "Default checkbox" }) }), _jsx(FormField, { inline: true, children: _jsx(Checkbox, { checked: switchLine, onChange: setSwitchLine, label: "Switch line", type: "switch-line" }) }), _jsx(FormField, { inline: true, children: _jsx(Checkbox, { checked: switchBox, onChange: setSwitchBox, label: "Switch box", type: "switch-box" }) }), _jsx(FormField, { inline: true, children: _jsx(Checkbox, { checked: intermediate, onChange: (v) => setIntermediate(v), label: "Intermediate (click to resolve)" }) }), _jsx(FormField, { inline: true, children: _jsx(Checkbox, { checked: true, onChange: () => { }, label: "Disabled checked", disabled: true }) }), _jsx(Value, { children: `checked: ${checked} | switchLine: ${switchLine} | switchBox: ${switchBox}` })] }));
};
// ─── ToggleFilter ─────────────────────────────────────────────────────────────
const ToggleFilterExample = () => {
    const [status, setStatus] = useState('active');
    const [country, setCountry] = useState('SG');
    return (_jsxs(Section, { title: "Toggle Filter", children: [_jsxs(FormField, { children: [_jsx(Label, { children: "Status filter" }), _jsx(ToggleFilter, { options: STATUS_OPTIONS, value: status, onChange: setStatus, fullWidth: true })] }), _jsxs(FormField, { children: [_jsx(Label, { children: "Responsive (collapses to Select below 200px)" }), _jsx(ToggleFilter, { options: COUNTRY_OPTIONS.slice(0, 4), value: country, onChange: setCountry, renderAsDropdown: { minWidth: 200 }, fullWidth: true })] }), _jsx(Value, { children: `status: "${status}" | country: "${country}"` })] }));
};
// ─── Date / Time pickers ──────────────────────────────────────────────────────
const DateTimeExample = () => {
    const [date, setDate] = useState('');
    const [time, setTime] = useState('');
    const [rangeStart, setRangeStart] = useState('');
    const [rangeEnd, setRangeEnd] = useState('');
    const [datetime, setDatetime] = useState('');
    const [trStart, setTrStart] = useState('');
    const [trEnd, setTrEnd] = useState('');
    return (_jsxs(Section, { title: "Date / Time Pickers", children: [_jsxs(FormField, { children: [_jsx(Label, { children: "DatePicker" }), _jsx(DatePicker, { date: date, onChange: setDate, closeOnSelect: true, fullWidth: true })] }), _jsxs(FormField, { children: [_jsx(Label, { children: "TimePicker" }), _jsx(TimePicker, { time: time, onChange: (d) => setTime(d), fullWidth: true })] }), _jsxs(FormField, { children: [_jsx(Label, { children: "DateRangePicker" }), _jsx(DateRangePicker, { startDate: rangeStart, endDate: rangeEnd, onChange: (s, e) => { setRangeStart(s); setRangeEnd(e); }, fullWidth: true })] }), _jsxs(FormField, { children: [_jsx(Label, { children: "DateTimePicker" }), _jsx(DateTimePicker, { datetime: datetime, onChange: (d) => setDatetime(d), fullWidth: true })] }), _jsxs(FormField, { children: [_jsx(Label, { children: "TimeRangePicker" }), _jsx(TimeRangePicker, { title: "Select time range", startTime: trStart, endTime: trEnd, onChange: (s, e) => { setTrStart(s); setTrEnd(e); }, fullWidth: true })] })] }));
};
// ─── Color & Icon pickers ─────────────────────────────────────────────────────
const PickersExample = () => {
    const [color, setColor] = useState('#3b82f6');
    const [icon, setIcon] = useState('');
    return (_jsxs(Section, { title: "Color & Icon Pickers", children: [_jsxs(FormField, { children: [_jsx(Label, { children: "ColorPicker" }), _jsx(ColorPicker, { color: color, onChange: setColor, fullWidth: true })] }), _jsxs(FormField, { children: [_jsx(Label, { children: "ColorPicker \u2014 no labels (dot only)" }), _jsx(ColorPicker, { color: color, onChange: setColor, hideLabels: true })] }), _jsxs(FormField, { children: [_jsx(Label, { children: "IconPicker" }), _jsx(IconPicker, { value: icon, onChange: setIcon, placeholder: "Select an icon\u2026", fullWidth: true })] }), _jsxs(FormField, { children: [_jsx(Label, { children: "IconPicker \u2014 compact mode" }), _jsx(IconPicker, { value: icon, onChange: setIcon, compactMode: true })] }), _jsx(Value, { children: `color: "${color}" | icon: "${icon}"` })] }));
};
// ─── FileInput ────────────────────────────────────────────────────────────────
const FileInputExample = () => {
    const [file1, setFile1] = useState('');
    const [file2, setFile2] = useState('');
    const [file3, setFile3] = useState('');
    const [file4, setFile4] = useState('');
    const [file5, setFile5] = useState('');
    return (_jsxs(Section, { title: "FileInput", children: [_jsxs(FormField, { children: [_jsx(Label, { children: "Basic (any file type)" }), _jsx(FileInput, { value: file1, onChange: (f) => setFile1(f), fullWidth: true })] }), _jsxs(FormField, { children: [_jsx(Label, { children: "Images only (with preview)" }), _jsx(FileInput, { value: file2, onChange: (f) => setFile2(f), allowedTypes: ['image/*'], preview: { showPreview: true, showName: true }, fullWidth: true })] }), _jsxs(FormField, { children: [_jsx(Label, { children: "PDF only" }), _jsx(FileInput, { value: file3, onChange: (f) => setFile3(f), allowedTypes: ['application/pdf'], dropAreaLabel: "Drop a PDF here", preview: { showName: true }, fullWidth: true })] }), _jsxs(FormField, { children: [_jsx(Label, { children: "Disabled" }), _jsx(FileInput, { value: '', onChange: () => { }, disabled: true, fullWidth: true })] }), _jsxs(FormField, { children: [_jsx(Label, { children: "Compact (any file)" }), _jsx(FileInput, { value: file4, onChange: (f) => setFile4(f), compact: true, fullWidth: true })] }), _jsxs(FormField, { children: [_jsx(Label, { children: "Compact \u2014 custom label & icon" }), _jsx(FileInput, { value: file5, onChange: (f) => setFile5(f), compact: true, allowedTypes: ['image/*'], dropAreaLabel: "Attach documents for this site", dropAreaIcon: ['fal', 'link'], fullWidth: true })] }), _jsxs(FormField, { children: [_jsx(Label, { children: "Compact \u2014 disabled" }), _jsx(FileInput, { value: '', onChange: () => { }, compact: true, disabled: true, fullWidth: true })] })] }));
};
// ─── InfoCard & InfoCardGroup mock data ───────────────────────────────────────
const MOCK_PEOPLE = [
    {
        id: 1,
        avatar: 'fas user',
        name: 'Alice Johnson',
        email: 'alice.johnson@example.com',
        department: 'Engineering',
        phone: '+1 415 555 0101',
        role: 'Senior Developer',
        location: 'San Francisco, CA',
    },
    {
        id: 2,
        avatar: 'fas user-tie',
        name: 'Bob Martinez',
        email: 'bob.martinez@example.com',
        department: 'Product',
        phone: '+1 415 555 0102',
        role: 'Product Manager',
        location: 'New York, NY',
    },
    {
        id: 3,
        avatar: 'fas user-circle',
        name: 'Carol Chen',
        email: 'carol.chen@example.com',
        department: 'Design',
        phone: '+1 415 555 0103',
        role: 'UX Designer',
        location: 'Austin, TX',
    },
    {
        id: 4,
        avatar: null,
        name: 'David Kim',
        email: 'david.kim@example.com',
        department: 'Marketing',
        phone: '+1 415 555 0104',
        role: 'Marketing Lead',
        location: 'Seattle, WA',
    },
    {
        id: 5,
        avatar: null,
        name: 'Eva Patel',
        email: 'eva.patel@example.com',
        department: 'Operations',
        phone: '+1 415 555 0105',
        role: 'Ops Manager',
        location: 'Chicago, IL',
    },
    {
        id: 6,
        avatar: null,
        name: 'Frank Lee',
        email: 'frank.lee@example.com',
        department: 'Engineering',
        phone: '+1 415 555 0106',
        role: 'QA Engineer',
        location: 'Boston, MA',
    },
];
const MOCK_LOCATIONS = [
    {
        id: 1,
        icon: 'fas building',
        name: 'HQ — Tower A',
        subtitle: 'Floor 12',
        type: 'Office',
        capacity: '120 seats',
        status: 'Active',
        manager: 'Alice Johnson',
    },
    {
        id: 2,
        icon: 'fas warehouse',
        name: 'Warehouse B',
        subtitle: 'Zone 3',
        type: 'Warehouse',
        capacity: '500 sq m',
        status: 'Active',
        manager: 'Bob Martinez',
    },
    {
        id: 3,
        icon: 'fas map-marker-alt',
        name: 'Remote Hub',
        subtitle: 'Co-working',
        type: 'Satellite Office',
        capacity: '30 desks',
        status: 'Under Maintenance',
        manager: 'Carol Chen',
    },
];
// ─── InfoCard Examples ────────────────────────────────────────────────────────
// const InfoCardExample: React.FC = () => {
//     const alice = MOCK_PEOPLE[0];
//     const bob = MOCK_PEOPLE[1];
//     const carol = MOCK_PEOPLE[2];
//     const david = MOCK_PEOPLE[3];
//     const location = MOCK_LOCATIONS[0];
//     return (
//         <div style={{
//             padding: '1.5rem',
//             background: 'var(--secondaryBGColor)',
//             borderRadius: '0.5rem',
//             display: 'flex',
//             flexDirection: 'column',
//             gap: '1.5rem',
//         }}>
//             <h4 style={{ margin: 0, color: 'var(--primaryTextColor)', borderBottom: '1px solid var(--borderColor)', paddingBottom: '0.5rem' }}>
//                 InfoCard
//             </h4>
//             {/* Row 1 — variants */}
//             <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1.5rem', alignItems: 'flex-start' }}>
//                 {/* Basic card — no details */}
//                 <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
//                     <Label>Card variant — no dropdown</Label>
//                     <InfoCard
//                         data={alice}
//                         fields={{ image: 'avatar', title: 'name', subtitle: 'email', name: 'name' }}
//                     />
//                 </div>
//                 {/* Card with ObjectInfoCard details */}
//                 <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
//                     <Label>Card — ObjectInfoCard details (click)</Label>
//                     <InfoCard
//                         data={bob}
//                         fields={{ image: 'avatar', title: 'name', subtitle: 'email', name: 'name' }}
//                         details={(item) => ({
//                             fields: [
//                                 { label: 'Department', value: item.department, icon: 'fas layer-group' },
//                                 { label: 'Role', value: item.role, icon: 'fas briefcase' },
//                                 { label: 'Phone', value: item.phone, icon: 'fas phone' },
//                                 { label: 'Location', value: item.location, icon: 'fas map-marker-alt' },
//                             ],
//                             columns: 1,
//                             layout: 'vertical',
//                         })}
//                         dropdownPosition="bottom-left"
//                     />
//                 </div>
//                 {/* Card with custom render details */}
//                 <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
//                     <Label>Card — custom render details (click)</Label>
//                     <InfoCard
//                         data={carol}
//                         fields={{ image: 'avatar', title: 'name', subtitle: 'email', name: 'name' }}
//                         details={(item) => (
//                             <div style={{ padding: '0.75rem', minWidth: '14rem' }}>
//                                 <div style={{ fontWeight: 600, color: 'var(--primaryTextColor)', marginBottom: '0.5rem' }}>{item.name}</div>
//                                 <div style={{ fontSize: '0.8rem', color: 'var(--secondaryTextColor)', marginBottom: '0.75rem' }}>{item.role} · {item.department}</div>
//                                 <a href={`mailto:${item.email}`} style={{ fontSize: '0.8rem', color: 'var(--accentColor)', textDecoration: 'none' }}>{item.email}</a>
//                             </div>
//                         )}
//                         dropdownPosition="bottom-left"
//                     />
//                 </div>
//                 {/* Card — initials (no image) */}
//                 <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
//                     <Label>Card — initials fallback</Label>
//                     <InfoCard
//                         data={david}
//                         fields={{ title: 'name', subtitle: 'email', name: 'name' }}
//                         details={(item) => ({
//                             fields: [
//                                 { label: 'Role', value: item.role },
//                                 { label: 'Department', value: item.department },
//                             ],
//                             columns: 1,
//                         })}
//                         dropdownPosition="bottom-left"
//                     />
//                 </div>
//             </div>
//             {/* Row 2 — avatar variant + non-person */}
//             <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1.5rem', alignItems: 'flex-start' }}>
//                 {/* Avatar variant — circle */}
//                 <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
//                     <Label>Avatar variant — circle (click)</Label>
//                     <InfoCard
//                         data={alice}
//                         fields={{ image: 'avatar', name: 'name' }}
//                         variant="avatar"
//                         size="large"
//                         shape="circle"
//                         details={(item) => ({
//                             fields: [
//                                 { label: 'Name', value: item.name },
//                                 { label: 'Email', value: item.email },
//                                 { label: 'Role', value: item.role },
//                             ],
//                             columns: 1,
//                         })}
//                     />
//                 </div>
//                 {/* Avatar variant — square */}
//                 <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
//                     <Label>Avatar variant — square</Label>
//                     <InfoCard
//                         data={carol}
//                         fields={{ image: 'avatar', name: 'name' }}
//                         variant="avatar"
//                         size="large"
//                         shape="square"
//                         details={(item) => ({
//                             fields: [
//                                 { label: 'Name', value: item.name },
//                                 { label: 'Department', value: item.department },
//                             ],
//                             columns: 1,
//                         })}
//                     />
//                 </div>
//                 {/* Non-person entity (location) */}
//                 <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
//                     <Label>Generic entity (location)</Label>
//                     <InfoCard
//                         data={location}
//                         fields={{ image: 'icon', title: 'name', subtitle: 'subtitle', name: 'name' }}
//                         // details={(item) => ({
//                         //     fields: [
//                         //         { label: 'Type', value: item.type },
//                         //         { label: 'Capacity', value: item.capacity },
//                         //         { label: 'Status', value: item.status },
//                         //         { label: 'Manager', value: item.manager },
//                         //     ],
//                         //     columns: 2,
//                         //     layout: 'vertical',
//                         // })}
//                         shape="square"
//                         dropdownPosition="bottom-left"
//                     />
//                 </div>
//                 {/* Sizes */}
//                 <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
//                     <Label>Sizes (small / medium / large)</Label>
//                     <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
//                         <InfoCard data={alice} fields={{ image: 'avatar', title: 'name', name: 'name' }} size="small" />
//                         <InfoCard data={bob} fields={{ image: 'avatar', title: 'name', name: 'name' }} size="medium" />
//                         <InfoCard data={carol} fields={{ image: 'avatar', title: 'name', name: 'name' }} size="large" />
//                     </div>
//                 </div>
//             </div>
//         </div>
//     );
// };
// ─── InfoCardGroup Examples ───────────────────────────────────────────────────
// const InfoCardGroupExample: React.FC = () => {
//     return (
//         <div style={{
//             padding: '1.5rem',
//             background: 'var(--secondaryBGColor)',
//             borderRadius: '0.5rem',
//             display: 'flex',
//             flexDirection: 'column',
//             gap: '1.5rem',
//         }}>
//             <h4 style={{ margin: 0, color: 'var(--primaryTextColor)', borderBottom: '1px solid var(--borderColor)', paddingBottom: '0.5rem' }}>
//                 InfoCardGroup
//             </h4>
//             <div style={{ display: 'flex', flexWrap: 'wrap', gap: '2rem', alignItems: 'flex-start' }}>
//                 {/* Basic group — no details */}
//                 <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
//                     <Label>Basic group (click to expand)</Label>
//                     <InfoCardGroup
//                         items={MOCK_PEOPLE}
//                         fields={{ image: 'avatar', name: 'name', title: 'name', subtitle: 'email' }}
//                         maxVisible={4}
//                     />
//                 </div>
//                 {/* Group with +N overflow */}
//                 <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
//                     <Label>Max 3 visible (+N badge)</Label>
//                     <InfoCardGroup
//                         items={MOCK_PEOPLE}
//                         fields={{ image: 'avatar', name: 'name', title: 'name', subtitle: 'email' }}
//                         maxVisible={3}
//                         size="medium"
//                     />
//                 </div>
//                 {/* Group with nested details */}
//                 <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
//                     <Label>With nested details (click item in dropdown)</Label>
//                     <InfoCardGroup
//                         items={MOCK_PEOPLE}
//                         fields={{ image: 'avatar', name: 'name', title: 'name', subtitle: 'role' }}
//                         maxVisible={4}
//                         // details={(item) => ({
//                         //     fields: [
//                         //         { label: 'Email', value: item.email, icon: 'fas envelope' },
//                         //         { label: 'Department', value: item.department, icon: 'fas layer-group' },
//                         //         { label: 'Phone', value: item.phone, icon: 'fas phone' },
//                         //         { label: 'Location', value: item.location, icon: 'fas map-marker-alt' },
//                         //     ],
//                         //     columns: 1,
//                         //     layout: 'vertical',
//                         // })}
//                         dropdownPosition="bottom-left"
//                     />
//                 </div>
//                 {/* Non-person group (locations) */}
//                 <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
//                     <Label>Generic entities (locations)</Label>
//                     <InfoCardGroup
//                         items={MOCK_LOCATIONS}
//                         fields={{ image: 'icon', name: 'name', title: 'name', subtitle: 'subtitle' }}
//                         maxVisible={5}
//                         shape="square"
//                         size="medium"
//                         // details={(item:a\) => ({
//                         //     fields: [
//                         //         { label: 'Type', value: item.type },
//                         //         { label: 'Capacity', value: item.capacity },
//                         //         { label: 'Status', value: item.status },
//                         //         { label: 'Manager', value: item.manager },
//                         //     ],
//                         //     columns: 1,
//                         // })}
//                     />
//                 </div>
//                 {/* Square + large */}
//                 <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
//                     <Label>Square shape, large size</Label>
//                     <InfoCardGroup
//                         items={MOCK_PEOPLE.slice(0, 4)}
//                         fields={{ image: 'avatar', name: 'name', title: 'name', subtitle: 'role' }}
//                         maxVisible={4}
//                         shape="square"
//                         size="large"
//                     />
//                 </div>
//                 {/* Empty state */}
//                 <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
//                     <Label>Empty state</Label>
//                     <InfoCardGroup
//                         items={[]}
//                         fields={{ image: 'avatar', name: 'name', title: 'name', subtitle: 'email' }}
//                     />
//                 </div>
//             </div>
//         </div>
//     );
// };
// ─── Shared table data ────────────────────────────────────────────────────────
const TABLE_COLUMNS = [
    { id: 'name', label: 'Name' },
    { id: 'email', label: 'Email' },
    { id: 'department', label: 'Department' },
    { id: 'role', label: 'Role' },
    { id: 'location', label: 'Location' },
    { id: 'status', label: 'Status' },
    { id: 'joined', label: 'Joined' },
    { id: 'phone', label: 'Phone' },
];
const TABLE_DATA = [
    { id: '1', name: 'Alice Johnson', email: 'alice@example.com', department: 'Engineering', role: 'Senior Dev', location: 'San Francisco', status: 'Active', joined: '2020-03-15', phone: '+1 415 555 0101' },
    { id: '2', name: 'Bob Martinez', email: 'bob@example.com', department: 'Product', role: 'Product Manager', location: 'New York', status: 'Active', joined: '2019-07-22', phone: '+1 212 555 0102' },
    { id: '3', name: 'Carol Chen', email: 'carol@example.com', department: 'Design', role: 'UX Designer', location: 'Austin', status: 'Active', joined: '2021-01-10', phone: '+1 512 555 0103' },
    { id: '4', name: 'David Kim', email: 'david@example.com', department: 'Marketing', role: 'Marketing Lead', location: 'Seattle', status: 'Inactive', joined: '2018-11-05', phone: '+1 206 555 0104' },
    { id: '5', name: 'Eva Patel', email: 'eva@example.com', department: 'Operations', role: 'Ops Manager', location: 'Chicago', status: 'Pending', joined: '2022-06-30', phone: '+1 312 555 0105' },
];
// ─── CodeEditor Example ───────────────────────────────────────────────────────
const JS_SAMPLE = `// Pre-process script — transform request before sending
function preProcess(inputs, context) {
    const { userId, token } = inputs;

    if (!userId) {
        throw new Error('userId is required');
    }

    return {
        headers: {
            Authorization: \`Bearer \${token}\`,
            'X-Request-ID': context.requestId,
        },
        body: {
            id: userId,
            timestamp: new Date().toISOString(),
        },
    };
}`;
const MD_SAMPLE = `# Authentication Model

Handles **user login**, token refresh, and session validation.

## Parameters

| Name | Type | Required | Description |
|------|------|----------|-------------|
| \`userId\` | string | ✓ | Unique user identifier |
| \`token\` | string | ✓ | JWT bearer token |
| \`refreshToken\` | string | | Refresh token for auto-renewal |

## Returns

\`\`\`json
{ "sessionKey": "abc123", "expiresAt": "2026-01-01T00:00:00Z" }
\`\`\`

> **Note:** Tokens expire after **24 hours**. Use \`refreshToken\` to renew silently.`;
const TS_SAMPLE = `interface ConnectorConfig {
    baseUrl: string;
    apiKey: string;
    timeout?: number;
    retries?: number;
}

async function callConnector(
    config: ConnectorConfig,
    endpoint: string,
    payload: Record<string, unknown>,
): Promise<unknown> {
    const response = await fetch(\`\${config.baseUrl}/\${endpoint}\`, {
        method: 'POST',
        headers: {
            'Authorization': \`Bearer \${config.apiKey}\`,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
    });

    if (!response.ok) {
        throw new Error(\`Connector error: \${response.status}\`);
    }

    return response.json();
}`;
const HTML_SAMPLE = `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8" />
    <title>Connector Test</title>
    <link rel="stylesheet" href="styles.css" />
</head>
<body>
    <div class="container">
        <h1 class="title">Connector Output</h1>
        <ul id="result-list"></ul>
    </div>
    <script src="main.js"></script>
</body>
</html>`;
const CSS_SAMPLE = `.container {
    max-width: 960px;
    margin: 0 auto;
    padding: 2rem;
    font-family: 'Satoshi', sans-serif;
}

.title {
    font-size: 1.75rem;
    font-weight: 700;
    color: var(--primaryTextColor);
    margin-bottom: 1rem;
}

#result-list {
    list-style: none;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
}

#result-list li {
    padding: 0.75rem 1rem;
    background: var(--secondaryBGColor);
    border-radius: 0.4rem;
    border: 1px solid var(--borderColor);
}`;
const JSON_SAMPLE = `{
    "events": [
        {
            "name": "OnDataReceived",
            "description": "Fired when new data arrives from the connector",
            "parameters": {
                "data": "object",
                "timestamp": "string"
            }
        },
        {
            "name": "OnError",
            "description": "Fired when the connector encounters an error",
            "parameters": {
                "code": "number",
                "message": "string"
            }
        }
    ]
}`;
const TABS = [
    { id: 'javascript', label: 'JavaScript', sample: JS_SAMPLE },
    { id: 'typescript', label: 'TypeScript', sample: TS_SAMPLE },
    { id: 'markdown', label: 'Markdown', sample: MD_SAMPLE },
    { id: 'html', label: 'HTML', sample: HTML_SAMPLE },
    { id: 'css', label: 'CSS', sample: CSS_SAMPLE },
    { id: 'json', label: 'JSON', sample: JSON_SAMPLE },
];
const INDENT_OPTIONS = [
    { label: '2 spaces', value: 2 },
    { label: '4 spaces', value: 4 },
    { label: '8 spaces', value: 8 },
];
const CodeEditorExample = () => {
    const [activeTab, setActiveTab] = useState('javascript');
    const [values, setValues] = useState({
        javascript: JS_SAMPLE,
        typescript: TS_SAMPLE,
        markdown: MD_SAMPLE,
        html: HTML_SAMPLE,
        css: CSS_SAMPLE,
        json: JSON_SAMPLE,
    });
    const [readOnly, setReadOnly] = useState(false);
    const [lineNumbers, setLineNumbers] = useState(true);
    const [indentUnit, setIndentUnit] = useState(4);
    const [saveResult, setSaveResult] = useState(null);
    const [hasSaved, setHasSaved] = useState(false);
    const editorRef = useRef(null);
    const handleSimulateSave = () => {
        var _a;
        const result = (_a = editorRef.current) === null || _a === void 0 ? void 0 : _a.validate();
        if (result) {
            setSaveResult(result);
            setHasSaved(true);
        }
    };
    const tabStyle = (active) => ({
        padding: '0.4rem 1rem',
        fontSize: '0.85rem',
        border: 'none',
        borderRadius: '0.3rem',
        cursor: 'pointer',
        background: active ? 'var(--primaryButtonBGColor)' : 'transparent',
        color: active ? 'var(--primaryButtonTextColor)' : 'var(--secondaryTextColor)',
        fontWeight: active ? 600 : 400,
    });
    return (_jsxs("div", { style: { display: 'flex', flexDirection: 'column', gap: '1rem', height: '100%' }, children: [_jsxs("div", { style: { display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }, children: [_jsx("div", { style: { display: 'flex', gap: '0.25rem', background: 'var(--secondaryBGColor)', padding: '0.25rem', borderRadius: '0.4rem' }, children: TABS.map(tab => (_jsx("button", { style: tabStyle(activeTab === tab.id), onClick: () => setActiveTab(tab.id), children: tab.label }, tab.id))) }), _jsxs("div", { style: { display: 'flex', gap: '0.5rem', marginLeft: 'auto', alignItems: 'center' }, children: [_jsxs("label", { style: { display: 'flex', alignItems: 'center', gap: '0.35rem', fontSize: '0.85rem', color: 'var(--secondaryTextColor)', cursor: 'pointer' }, children: [_jsx("input", { type: "checkbox", checked: lineNumbers, onChange: e => setLineNumbers(e.target.checked) }), "Line numbers"] }), _jsxs("label", { style: { display: 'flex', alignItems: 'center', gap: '0.35rem', fontSize: '0.85rem', color: 'var(--secondaryTextColor)', cursor: 'pointer' }, children: [_jsx("input", { type: "checkbox", checked: readOnly, onChange: e => setReadOnly(e.target.checked) }), "Read-only"] }), _jsx("select", { value: indentUnit, onChange: e => setIndentUnit(Number(e.target.value)), style: { fontSize: '0.85rem', padding: '0.3rem 0.5rem', borderRadius: '0.3rem', border: '1px solid var(--inputBorderColor)', background: 'var(--inputBGColor)', color: 'var(--primaryTextColor)', cursor: 'pointer' }, children: INDENT_OPTIONS.map(o => (_jsx("option", { value: o.value, children: o.label }, o.value))) }), _jsx(ButtonComponent, { title: "Focus editor", size: "small", variant: "secondary", onClick: () => { var _a; return (_a = editorRef.current) === null || _a === void 0 ? void 0 : _a.focus(); } }), _jsx(ButtonComponent, { title: "Reset", size: "small", variant: "secondary", onClick: () => { setValues(prev => (Object.assign(Object.assign({}, prev), { [activeTab]: TABS.find(t => t.id === activeTab).sample }))); setSaveResult(null); setHasSaved(false); } }), _jsx(ButtonComponent, { title: "Simulate Save", size: "small", variant: "primary", onClick: handleSimulateSave })] })] }), _jsx(CodeEditor, { ref: editorRef, value: values[activeTab], onChange: val => { setValues(prev => (Object.assign(Object.assign({}, prev), { [activeTab]: val }))); setSaveResult(null); setHasSaved(false); }, language: activeTab, lineNumbers: lineNumbers, readOnly: readOnly, fullWidth: true }), hasSaved && saveResult && (saveResult.valid
                ? _jsx("div", { style: { fontSize: '0.85rem', color: 'var(--successTextColor, #16a34a)' }, children: "Valid \u2014 content saved successfully" })
                : _jsxs("div", { style: { padding: '0.5rem 0.75rem', borderRadius: '0.4rem', background: 'var(--errorBGColor, #fef2f2)', border: '1px solid var(--errorBorderColor, #fca5a5)', color: 'var(--errorTextColor, #dc2626)', fontSize: '0.85rem', fontFamily: 'monospace' }, children: ["Error: ", saveResult.error] })), _jsxs("div", { style: { fontSize: '0.75rem', color: 'var(--secondaryTextColor)', textAlign: 'right' }, children: [values[activeTab].length, " characters \u00B7 ", values[activeTab].split('\n').length, " lines"] })] }));
};
// ─── MarkdownEditor Example ───────────────────────────────────────────────────
const MD_EDITOR_SAMPLE = `# Welcome to MarkdownEditor

This editor supports **bold**, *italic*, and \`inline code\`.

## Features

- Write tab with full syntax highlighting
- Preview tab with UXP prose styles
- Supports headings, lists, code blocks, blockquotes, and tables

## Code Example

\`\`\`js
async function run(inputs) {
    const data = await fetch('/api/data');
    return data.json();
}
\`\`\`

> Switch to **Preview** to see the rendered output.`;
const MarkdownEditorExample = () => {
    const [value, setValue] = useState(MD_EDITOR_SAMPLE);
    return (_jsxs("div", { style: { display: 'flex', flexDirection: 'column', gap: '1rem', height: '100%' }, children: [_jsx("div", { style: { display: 'flex', justifyContent: 'flex-end' }, children: _jsx(ButtonComponent, { title: "Reset", size: "small", variant: "secondary", onClick: () => setValue(MD_EDITOR_SAMPLE) }) }), _jsx(MarkdownEditor, { value: value, onChange: setValue, fullWidth: true, lineNumbers: true, previewEmptyText: "Nothing to preview yet." })] }));
};
// ─── Chip Example ────────────────────────────────────────────────────────────
const CHIP_VARIANTS = ['primary', 'secondary', 'success', 'warning', 'danger', 'info'];
const CHIP_SIZES = ['small', 'medium', 'large'];
const ChipRow = ({ label, children }) => (_jsxs("div", { style: { display: 'flex', flexDirection: 'column', gap: '0.5rem' }, children: [_jsx("div", { style: { fontSize: '0.75rem', color: 'var(--secondaryTextColor)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }, children: label }), _jsx("div", { style: { display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.5rem' }, children: children })] }));
const ChipExample = () => {
    const [clickedVariant, setClickedVariant] = useState('—');
    return (_jsxs("div", { style: { display: 'flex', flexDirection: 'column', gap: '2rem', padding: '0.5rem 0' }, children: [_jsx(ChipRow, { label: "Variants (medium, label only)", children: CHIP_VARIANTS.map(v => (_jsx(Chip, { label: v.charAt(0).toUpperCase() + v.slice(1), variant: v }, v))) }), _jsx(ChipRow, { label: "Variants \u2014 dot icon (status style)", children: CHIP_VARIANTS.map(v => (_jsx(Chip, { label: v.charAt(0).toUpperCase() + v.slice(1), variant: v, icon: "fas circle" }, v))) }), _jsx(ChipRow, { label: "Variants \u2014 left icon", children: [
                    { v: 'primary', icon: 'fal star' },
                    { v: 'secondary', icon: 'fal circle' },
                    { v: 'success', icon: 'fal check-circle' },
                    { v: 'warning', icon: 'fal lock' },
                    { v: 'danger', icon: 'fal times-circle' },
                    { v: 'info', icon: 'fal info-circle' },
                ].map(({ v, icon }) => (_jsx(Chip, { label: v.charAt(0).toUpperCase() + v.slice(1), variant: v, icon: icon }, v))) }), _jsx(ChipRow, { label: "Variants \u2014 right icon", children: CHIP_VARIANTS.map(v => (_jsx(Chip, { label: v.charAt(0).toUpperCase() + v.slice(1), variant: v, icon: "fal arrow-right", iconPosition: "right" }, v))) }), _jsx(ChipRow, { label: "Sizes (success variant)", children: CHIP_SIZES.map(s => (_jsx(Chip, { label: s.charAt(0).toUpperCase() + s.slice(1), variant: "success", icon: "fas circle", size: s }, s))) }), _jsx(ChipRow, { label: "Sizes (warning variant)", children: CHIP_SIZES.map(s => (_jsx(Chip, { label: s.charAt(0).toUpperCase() + s.slice(1), variant: "warning", icon: "fal lock", size: s }, s))) }), _jsx(ChipRow, { label: `Clickable — last clicked: ${clickedVariant}`, children: CHIP_VARIANTS.map(v => (_jsx(Chip, { label: v.charAt(0).toUpperCase() + v.slice(1), variant: v, icon: "fal hand-pointer", onClick: () => setClickedVariant(v) }, v))) }), _jsxs(ChipRow, { label: "Active / Inactive / Readonly (ModelStatusChips pattern)", children: [_jsx(Chip, { label: "Active", variant: "success", icon: "fas circle", size: "small" }), _jsx(Chip, { label: "Inactive", variant: "secondary", icon: "fas circle", size: "small" }), _jsx(Chip, { label: "Read-only", variant: "warning", icon: "fal lock", size: "small" })] }), _jsxs(ChipRow, { label: "Custom colors (backgroundColor + textColor props)", children: [_jsx(Chip, { label: "Custom purple", backgroundColor: "#7c3aed", textColor: "#ffffff", icon: "fal gem" }), _jsx(Chip, { label: "Custom teal", backgroundColor: "rgba(20,184,166,0.15)", textColor: "#0f766e", icon: "fal leaf" }), _jsx(Chip, { label: "No variant", backgroundColor: "transparent", textColor: "var(--primaryTextColor)", icon: "fal tag" })] })] }));
};
// ─── ObjectSearch Example ─────────────────────────────────────────────────────
// Mirrors real app usage (Lucy ModelsView): function data source with pagination,
// filters, sort, and an auto-refresh toggle firing silentRefreshCurrentPage every 2s —
// used to verify user changes (filter panel edits, sort, columns) survive refreshes.
const OSC_DEPARTMENTS = ['Engineering', 'Product', 'Design', 'Marketing', 'Operations'];
const OSC_STATUSES = ['Active', 'Inactive', 'Pending'];
const OSC_LOCATIONS = ['San Francisco', 'New York', 'Austin', 'Seattle', 'Chicago', 'Singapore', 'London'];
const OSC_ROLES = ['Developer', 'Senior Dev', 'Manager', 'Designer', 'Analyst', 'Lead'];
const OSC_FIRST_NAMES = ['Alice', 'Bob', 'Carol', 'David', 'Eva', 'Frank', 'Grace', 'Henry', 'Isla', 'Jack', 'Kira', 'Liam', 'Maya', 'Noah', 'Olivia', 'Peter'];
const OSC_LAST_NAMES = ['Johnson', 'Martinez', 'Chen', 'Kim', 'Patel', 'Silva', 'Weber', 'Tanaka', 'Novak', 'Costa'];
const pickRandom = (arr) => arr[Math.floor(Math.random() * arr.length)];
function generateTeamMembers(count) {
    const rows = [];
    for (let i = 0; i < count; i++) {
        const name = `${pickRandom(OSC_FIRST_NAMES)} ${pickRandom(OSC_LAST_NAMES)}`;
        rows.push({
            id: String(i + 1),
            name,
            email: `${name.toLowerCase().replace(' ', '.')}.${i + 1}@example.com`,
            department: pickRandom(OSC_DEPARTMENTS),
            role: pickRandom(OSC_ROLES),
            location: pickRandom(OSC_LOCATIONS),
            status: pickRandom(OSC_STATUSES),
            // random datetime within the last ~3 years
            joined: new Date(Date.now() - Math.floor(Math.random() * 3 * 365 * 24 * 60 * 60 * 1000)).toISOString(),
        });
    }
    return rows;
}
const OSC_SAMPLE_ROWS = generateTeamMembers(120);
function filterOscRows(query, filters) {
    var _a, _b, _c;
    let rows = OSC_SAMPLE_ROWS;
    const q = (query || '').toLowerCase().trim();
    if (q)
        rows = rows.filter(r => String(r.name).toLowerCase().includes(q) || String(r.email).toLowerCase().includes(q));
    const department = (_a = filters === null || filters === void 0 ? void 0 : filters.filters) === null || _a === void 0 ? void 0 : _a.department;
    if (department)
        rows = rows.filter(r => r.department === department);
    const status = (_b = filters === null || filters === void 0 ? void 0 : filters.filters) === null || _b === void 0 ? void 0 : _b.status;
    if (status)
        rows = rows.filter(r => r.status === status);
    const joinedAfter = (_c = filters === null || filters === void 0 ? void 0 : filters.filters) === null || _c === void 0 ? void 0 : _c.joinedAfter;
    if (joinedAfter) {
        const after = new Date(joinedAfter);
        if (!isNaN(after.getTime()))
            rows = rows.filter(r => new Date(String(r.joined)) >= after);
    }
    return rows;
}
const OSC_COLUMNS = [
    { id: 'name', label: 'Name', isSortable: true },
    { id: 'email', label: 'Email' },
    { id: 'department', label: 'Department', isSortable: true },
    { id: 'role', label: 'Role' },
    { id: 'location', label: 'Location' },
    { id: 'status', label: 'Status', isSortable: true },
    { id: 'joined', label: 'Joined', isSortable: true, renderColumn: (item) => _jsx(_Fragment, { children: new Date(String(item.joined)).toLocaleString() }) },
    { id: 'score', label: 'Score', isSortable: true },
    { id: 'updated', label: 'Updated' },
];
const ObjectSearchExample = () => {
    const oscRef = useRef(null);
    const [autoRefresh, setAutoRefresh] = useState(false);
    useEffect(() => {
        if (!autoRefresh)
            return;
        const timer = setInterval(() => { var _a; return (_a = oscRef.current) === null || _a === void 0 ? void 0 : _a.silentRefreshCurrentPage(); }, 2000);
        return () => clearInterval(timer);
    }, [autoRefresh]);
    const getData = useCallback((page, pageSize, query, filters, sort) => __awaiter(void 0, void 0, void 0, function* () {
        yield new Promise(resolve => setTimeout(resolve, 300)); // simulate network
        let rows = filterOscRows(query, filters);
        const [sortKey, sortDir] = Object.entries(sort || {}).find(([, d]) => d !== 0) || [];
        if (sortKey)
            rows = [...rows].sort((a, b) => sortDir * String(a[sortKey]).localeCompare(String(b[sortKey]), undefined, { numeric: true }));
        const start = (page - 1) * pageSize;
        // volatile fields — every (silent) refresh visibly changes the rows
        return {
            items: rows.slice(start, start + pageSize).map(r => (Object.assign(Object.assign({}, r), { score: Math.round(Math.random() * 100), updated: new Date().toLocaleTimeString() })))
        };
    }), []);
    const getTotal = useCallback((query, filters) => __awaiter(void 0, void 0, void 0, function* () {
        return filterOscRows(query, filters).length;
    }), []);
    const filterConfig = useMemo(() => ({
        formFields: [{
                title: '',
                columns: 1,
                fields: [
                    {
                        name: 'department',
                        label: 'Department',
                        type: 'select',
                        options: [{ label: 'All', value: '' }, ...OSC_DEPARTMENTS.map(d => ({ label: d, value: d }))],
                    },
                    {
                        name: 'status',
                        label: 'Status',
                        type: 'select',
                        options: [{ label: 'All', value: '' }, ...OSC_STATUSES.map(s => ({ label: s, value: s }))],
                    },
                    {
                        name: 'joinedAfter',
                        label: 'Joined after',
                        type: 'datetime',
                    },
                ],
            }],
    }), []);
    const actionButtons = (_jsx(ButtonComponent, { title: autoRefresh ? 'Stop Auto Refresh' : 'Auto Refresh (2s)', leftIcon: autoRefresh ? 'fas stop' : 'fas play', variant: autoRefresh ? 'danger' : 'primary', onClick: () => setAutoRefresh(p => !p) }));
    return (_jsx(ObjectSearchComponent, { ref: oscRef, title: "Team Members", data: getData, columns: OSC_COLUMNS, idField: "id", pageSize: 10, total: getTotal, search: { enable: true, fields: ['name', 'email'] }, filters: filterConfig, actionButtons: actionButtons, defaultActionColumns: {
            actionsColumn: {
                enable: true,
                headerActions: { sort: true, customiseColumns: true },
            },
        }, appendToURL: false }));
};
// ─── SimpleConfigurationTable Example ────────────────────────────────────────
const SimpleConfigTableExample = () => {
    const [data, setData] = useState(TABLE_DATA.map(r => (Object.assign({}, r))));
    return (_jsx(SimpleConfigurationTable, { data: data, columns: TABLE_COLUMNS, search: { enable: true }, disablePagination: true, onAddItem: (item) => __awaiter(void 0, void 0, void 0, function* () {
            setData(prev => [...prev, Object.assign({ id: String(prev.length + 1) }, item)]);
            return { success: true };
        }) }));
};
// ─── Tables inside ConfigurationView ─────────────────────────────────────────
// ─── PillInput Example ────────────────────────────────────────────────────────
const PILL_SECTIONS = [
    {
        label: 'Asset',
        fields: [
            { label: 'Asset Name', value: '$.asset.name' },
            { label: 'Asset ID', value: '$.asset.id' },
            { label: 'Asset Type', value: '$.asset.type' },
            { label: 'Status', value: '$.asset.status' },
        ],
    },
    {
        label: 'Location',
        fields: [
            { label: 'Building', value: '$.location.building' },
            { label: 'Floor', value: '$.location.floor' },
            { label: 'Room', value: '$.location.room' },
        ],
    },
];
const PillInputExample = () => {
    const [value, setValue] = useState('Hello {$.asset.name} on floor {$.location.floor}');
    return (_jsxs("div", { style: { display: 'flex', flexDirection: 'column', gap: '1rem' }, children: [_jsxs(FormField, { children: [_jsx(Label, { children: "Template expression" }), _jsx(PillInput, { value: value, onChange: setValue, contextDataSections: PILL_SECTIONS, placeholder: "Type or click a field to insert a pill...", fullWidth: true })] }), _jsxs("div", { style: { fontSize: '0.75rem', color: 'var(--secondaryTextColor)' }, children: ["Value: ", _jsx("code", { style: { wordBreak: 'break-all' }, children: value || '—' })] }), _jsx(ButtonComponent, { title: "Reset", size: "small", variant: "secondary", onClick: () => setValue('Hello {$.asset.name} on floor {$.location.floor}') })] }));
};
// ─── InfoCard Examples ────────────────────────────────────────────────────────
const MOCK_USER = { avatar: null, name: 'Dinesh Gamage', email: 'dinesh@ecyber.com', group: 'System Administrator' };
const MOCK_LOCATION = { thumbnail: 'https://images.unsplash.com/photo-1486325212027-8081e485255e?w=64&h=64&fit=crop', name: 'Singapore', type: 'Site' };
const InfoCardExample = () => {
    const activeChip = _jsx(Chip, { label: "Active", variant: "success", size: "small" });
    const locationFields = [
        { value: 'ap-south-1', style: 'muted', icon: 'fas globe' },
    ];
    return (_jsxs("div", { style: { display: 'flex', flexDirection: 'column', gap: '1.25rem' }, children: [_jsx(InfoCard, { data: MOCK_USER, fields: { name: 'name', title: 'name', subtitle: 'email' }, extraFields: [{ value: MOCK_USER.group, style: 'muted' }], badges: activeChip }), _jsx(InfoCard, { data: MOCK_LOCATION, fields: { image: 'thumbnail', title: 'name', subtitle: 'type' }, extraFields: locationFields, badges: activeChip, shape: "square", size: "medium" }), _jsx(InfoCard, { data: MOCK_LOCATION, fields: { image: 'thumbnail', name: 'name', title: 'name', subtitle: 'type' }, badges: activeChip, shape: "square", size: "medium", layout: "collapsed", titleStyle: "link", onTitleClick: () => alert('title clicked') }), _jsx(InfoCard, { data: { name: 'Maintenance Report 2025' }, fields: { title: 'name' }, extraFields: [
                    { value: '34.94 KB', style: 'muted' },
                    { value: 'View document', style: 'link', icon: 'fas external-link-alt', onClick: () => alert('open') },
                ], badges: _jsx(Chip, { label: "Document", size: "small" }) })] }));
};
// ─── Notification Test ────────────────────────────────────────────────────────
const SEVERITY_OPTIONS = [
    { label: 'Critical', value: 'CRITICAL' },
    { label: 'High', value: 'HIGH' },
    { label: 'Medium', value: 'MEDIUM' },
    { label: 'Low', value: 'LOW' },
];
const NotificationTestExample = () => {
    const context = useContext(UXPContext);
    const [notificationId, setNotificationId] = useState('equipment-fault-EQUIP-001');
    const [objectType, setObjectType] = useState('IBMSEquipment');
    const [objectKey, setObjectKey] = useState('EQUIP-001');
    const [severity, setSeverity] = useState('HIGH');
    const [message, setMessage] = useState('Equipment EQUIP-001 fault detected');
    const [status, setStatus] = useState(null);
    const handlePublish = () => __awaiter(void 0, void 0, void 0, function* () {
        var _a;
        setStatus('Publishing…');
        const result = yield context.publishNotification({
            notificationId,
            objectType,
            severity: severity,
            message,
            objectKey,
        });
        setStatus(result.error ? `Error: ${result.errorMessage}` : `Published — created: ${(_a = result.data) === null || _a === void 0 ? void 0 : _a.created}`);
    });
    const handleResolve = () => __awaiter(void 0, void 0, void 0, function* () {
        var _a;
        setStatus('Resolving…');
        const result = yield context.resolveNotification(notificationId, objectType);
        setStatus(result.error ? `Error: ${result.errorMessage}` : `Resolved — id: ${(_a = result.data) === null || _a === void 0 ? void 0 : _a.notificationID}`);
    });
    return (_jsxs("div", { style: { display: 'flex', flexDirection: 'column', gap: '1rem', height: '100%' }, children: [_jsxs(FormField, { children: [_jsx(Label, { children: "Notification ID" }), _jsx(Input, { value: notificationId, onChange: setNotificationId, fullWidth: true })] }), _jsxs(FormField, { children: [_jsx(Label, { children: "Object Type" }), _jsx(Input, { value: objectType, onChange: setObjectType, fullWidth: true })] }), _jsxs(FormField, { children: [_jsx(Label, { children: "Object Key" }), _jsx(Input, { value: objectKey, onChange: setObjectKey, fullWidth: true })] }), _jsxs(FormField, { children: [_jsx(Label, { children: "Severity" }), _jsx(Select, { options: SEVERITY_OPTIONS, selected: severity, onChange: setSeverity, fullWidth: true })] }), _jsxs(FormField, { children: [_jsx(Label, { children: "Message" }), _jsx(TextArea, { value: message, onChange: setMessage, rows: 2, fullWidth: true })] }), _jsxs("div", { style: { display: 'flex', gap: '0.5rem' }, children: [_jsx(ButtonComponent, { title: "Publish", onClick: handlePublish }), _jsx(ButtonComponent, { title: "Resolve", variant: "secondary", onClick: handleResolve })] }), status && _jsx(Value, { children: status })] }));
};
const TablesConfigViewExample = () => {
    const context = useContext(UXPContext);
    return (_jsx("div", { style: { padding: '1rem', height: '100%' }, children: _jsx(ConfigurationView, { uxpContext: context, title: "Components", sections: [
                {
                    id: 'notifications',
                    title: 'Notifications',
                    content: _jsx(NotificationTestExample, {}),
                },
                {
                    id: 'info-card',
                    title: 'Info Card',
                    content: _jsx(InfoCardExample, {}),
                },
                {
                    id: 'chip',
                    title: 'Chip',
                    content: _jsx(ChipExample, {}),
                },
                {
                    id: 'pill-input',
                    title: 'Pill Input',
                    content: _jsx(PillInputExample, {}),
                },
                {
                    id: 'code-editor',
                    title: 'Code Editor',
                    content: _jsx(CodeEditorExample, {}),
                },
                {
                    id: 'markdown-editor',
                    title: 'Markdown Editor',
                    content: _jsx(MarkdownEditorExample, {}),
                },
                {
                    id: 'object-search',
                    title: 'Object Search',
                    content: _jsx(ObjectSearchExample, {}),
                    actions: _jsx(_Fragment, { children: _jsx(ButtonComponent, { title: "Test" }) })
                },
                {
                    id: 'simple-config-table',
                    title: 'Simple Config Table',
                    content: _jsx(SimpleConfigTableExample, {}),
                    actions: _jsx(_Fragment, { children: _jsx(ButtonComponent, { title: "Test" }) })
                },
            ] }) }));
};
// ─── Showcase root ────────────────────────────────────────────────────────────
const UXPShowcase = () => {
    return (_jsx("div", { style: {
            position: 'relative',
            width: '100%',
            height: '100%',
            // backgroundColor: 'var(--primaryBGColor)',
            backgroundImage: 'url(https://ipfs.io/ipfs/QmZtLh7n1Zy8c5jHjvVh3XqYjL2G9mN1g2u5sK6f8r9xR/texture1.png)',
            overflowY: 'auto',
        }, children: _jsx(ObjectSearchExample, {}) }));
};
export default UXPShowcase;
